import { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import Dropzone from 'react-dropzone';
import { toast } from 'react-toastify';
import { formatBytes } from 'client/js/util/formatters';
import { useUploadFile } from './hooks/use_filemanager';

const PARALLEL_UPLOADS = 5; // Fixed number of parallel uploads

const UploadModal = ({ currentPath, onClose, initialFiles = [], baseApiUrl }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState('files'); // 'files' or 'folder'
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, currentFile: '' });
  const [cancelRequested, setCancelRequested] = useState(false);
  const [toastId, setToastId] = useState(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const uploadFileMutation = useUploadFile(baseApiUrl);

  useEffect(() => {
    if (initialFiles.length > 0) {
      setFiles(initialFiles);
    }
  }, [initialFiles]);

  const onDrop = useCallback((acceptedFiles) => {
    setFiles(prevFiles => [...prevFiles, ...acceptedFiles]);
  }, []);

  const handleFolderSelect = useCallback((e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prevFiles => [...prevFiles, ...selectedFiles]);
    setUploadMode('folder');
  }, []);

  const handleRemoveFile = (index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const updateToast = useCallback((current, total, currentFile, onCancel) => {
    const progressPercentage = total > 0 ? Math.round((current / total) * 100) : 0;

    const toastContent = (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong>Upload läuft...</strong>
          <button
            type="button"
            className="btn btn-sm btn-link p-0 text-danger"
            onClick={onCancel}
            style={{ fontSize: '1.2rem', lineHeight: 1 }}
          >
            <i className="fa fa-times" />
          </button>
        </div>
        <div className="progress mb-2" style={{ height: '8px' }}>
          <div
            className="progress-bar progress-bar-striped progress-bar-animated"
            role="progressbar"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        <small>{current} / {total} Dateien</small>
      </div>
    );

    return toastContent;
  }, []);

  const handleUpload = async () => {
    if (files.length === 0) {
      alert('Bitte wählen Sie mindestens eine Datei aus');
      return;
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: files.length, currentFile: '' });
    setCancelRequested(false);

    const REFRESH_INTERVAL = 20; // Refresh file list every 20 uploads
    let completedCount = 0;
    let cancelRequestedRef = false;

    const handleCancelUpload = () => {
      cancelRequestedRef = true;
      setCancelRequested(true);
    };

    // Create persistent toast without close button
    const id = toast.info(updateToast(0, files.length, '', handleCancelUpload), {
      autoClose: false,
      closeButton: false,
      closeOnClick: false,
      draggable: false,
    });
    setToastId(id);

    try {
      // Process files in batches for parallel uploads
      for (let batchStart = 0; batchStart < files.length; batchStart += PARALLEL_UPLOADS) {
        // Check if cancel was requested
        if (cancelRequestedRef) {
          break;
        }

        const batchEnd = Math.min(batchStart + PARALLEL_UPLOADS, files.length);
        const batch = files.slice(batchStart, batchEnd);

        // Upload batch in parallel
        const uploadPromises = batch.map(async (file, batchIndex) => {
          const globalIndex = batchStart + batchIndex;
          const relativePath = file.webkitRelativePath || null;
          const displayName = relativePath || file.name;

          // Update progress with current file
          setUploadProgress(prev => ({
            ...prev,
            currentFile: displayName
          }));

          // Only refresh list every REFRESH_INTERVAL uploads or on the last file
          const isLastFile = globalIndex === files.length - 1;
          const shouldRefresh = isLastFile || ((globalIndex + 1) % REFRESH_INTERVAL === 0);

          try {
            await uploadFileMutation.mutateAsync({
              path: currentPath,
              file,
              relativePath,
              skipRefresh: !shouldRefresh
            });

            completedCount++;
            const newProgress = {
              current: completedCount,
              total: files.length,
              currentFile: displayName
            };
            setUploadProgress(newProgress);

            // Update toast
            toast.update(id, {
              render: updateToast(completedCount, files.length, displayName, handleCancelUpload)
            });
          } catch (error) {
            console.error(`Upload error for ${displayName}:`, error);
            throw error;
          }
        });

        // Wait for all uploads in this batch to complete
        await Promise.all(uploadPromises);
      }

      if (!cancelRequestedRef) {
        toast.update(id, {
          render: `${completedCount} Dateien erfolgreich hochgeladen`,
          type: 'success',
          autoClose: 3000,
          closeButton: true,
          draggable: true
        });
        onClose();
      } else {
        toast.update(id, {
          render: `Upload abgebrochen. ${completedCount} von ${files.length} Dateien hochgeladen.`,
          type: 'warning',
          autoClose: 5000,
          closeButton: true,
          draggable: true
        });
        onClose();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.update(id, {
        render: 'Upload fehlgeschlagen',
        type: 'error',
        autoClose: 5000,
        closeButton: true,
        draggable: true
      });
    } finally {
      setUploading(false);
      // id is handled by updates, no need to dismiss unless we want to force close on unmount/error
    }
  };

  const handleCancel = () => {
    onClose();
  };

  // Don't show modal during upload - toast handles progress display
  if (uploading) {
    return null;
  }

  return (
    <Modal
      isOpen={true}
      onRequestClose={handleCancel}
      contentLabel="Dateien hochladen"
      className="modal-dialog modal-lg"
      overlayClassName="modal-backdrop"
    >
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">Dateien hochladen</h5>
          <button type="button" className="close" onClick={handleCancel}>
            <span>&times;</span>
          </button>
        </div>
        <div className="modal-body">
          <div className="mb-3">
            <div className="btn-group" role="group">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => fileInputRef.current?.click()}
              >
                <i className="fa fa-file mr-2" />
                Dateien auswählen
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => folderInputRef.current?.click()}
              >
                <i className="fa fa-folder mr-2" />
                Ordner auswählen
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                const selectedFiles = Array.from(e.target.files);
                setFiles(prevFiles => [...prevFiles, ...selectedFiles]);
                setUploadMode('files');
              }}
            />
            <input
              ref={folderInputRef}
              type="file"
              webkitdirectory="true"
              directory="true"
              multiple
              style={{ display: 'none' }}
              onChange={handleFolderSelect}
            />
          </div>

          <Dropzone
            onDrop={onDrop}
            style={{
              border: '2px dashed #ccc',
              borderRadius: '4px',
              padding: '40px',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: '#fafafa'
            }}
            activeStyle={{
              backgroundColor: '#f0f0f0',
              borderColor: '#007bff'
            }}
          >
            <div>
              <i className="fa fa-cloud-upload fa-3x text-muted mb-3" />
              <p>Dateien hierhin ziehen oder oben auswählen</p>
              <small className="text-muted">Ordner können nur über den Button ausgewählt werden</small>
            </div>
          </Dropzone>

          {files.length > 0 && (
            <div className="mt-3">
              <h6>Ausgewählte Dateien ({files.length}):</h6>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <ul className="list-group">
                  {files.map((file, index) => (
                    <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                      <span>
                        <i className={`fa ${file.webkitRelativePath ? 'fa-folder-open' : 'fa-file'} mr-2`} />
                        {file.webkitRelativePath || file.name}
                        <small className="text-muted ml-2">({formatBytes(file.size)})</small>
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleRemoveFile(index)}
                      >
                        <i className="fa fa-times" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
          >
            Abbrechen
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={files.length === 0}
          >
            <i className="fa fa-upload mr-2" />
            Hochladen
          </button>
        </div>
      </div>
    </Modal>
  );
};

UploadModal.propTypes = {
  currentPath: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  initialFiles: PropTypes.array,
  baseApiUrl: PropTypes.string,
};

export default UploadModal;